import {
  useArtifactCanvas,
  PageContainer,
  PageHeader,
  PageHeaderLeading,
  PersonListCard,
  PaneResponsiveCardGrid,
  DashboardSectionSkeleton,
  Button,
  formatClientDateTimeMedium,
  employerRecentApplicantToPersonModel,
  MatchQualityBadge,
  getHrefWithThreadPreservation,
} from '@yeshuman/sdk'
import { useNavigate } from 'react-router-dom'
import { CalendarPlus, ClipboardList, Video } from 'lucide-react'

import { ScheduleInterviewCanvas } from './schedule-interview-canvas'
import { InterviewKitPanel } from '../interview/interview-kit-panel'
import { useInterviewerCandidates } from '../../hooks/use-interviewer-candidates'
import { interviewerCandidateSessionHref } from '../../lib/interviewer-session'
import { InterviewerBriefContextLine } from './interviewer-brief-context'

export function InterviewerCandidatesPage() {
  const navigate = useNavigate()
  const { openCanvas, closeCanvas } = useArtifactCanvas()
  const { data: candidates = [], isLoading, error } = useInterviewerCandidates()

  return (
    <PageContainer>
      <PageHeader
        leading={<PageHeaderLeading />}
        title="Candidates"
        description="People in the interview stage. Interview now, or book a time and find it under Interviews."
      />

      {isLoading ? (
        <DashboardSectionSkeleton cards={4} grid />
      ) : error ? (
        <p className="text-sm text-muted-foreground">Unable to load candidates.</p>
      ) : candidates.length === 0 ? (
        <p className="text-sm text-muted-foreground">No one is waiting to be interviewed.</p>
      ) : (
        <PaneResponsiveCardGrid>
          {candidates.map((row) => {
            const person = employerRecentApplicantToPersonModel(row)
            const interviewHref = getHrefWithThreadPreservation(
              interviewerCandidateSessionHref(row, {
                label: person.name,
                interviewId: row.next_interview?.id,
              }),
            )
            const analysisId = row.evaluation_id ?? row.recruiter_evaluation_id
            return (
              <PersonListCard
                key={row.id}
                variant="compact"
                person={person}
                meta={
                  row.next_interview
                    ? formatClientDateTimeMedium(row.next_interview.scheduled_start)
                    : row.applied_at
                      ? `Applied ${new Date(row.applied_at).toLocaleDateString()}`
                      : undefined
                }
                body={<InterviewerBriefContextLine context={row} />}
                headerAside={
                  typeof row.match_score === 'number' ? (
                    <MatchQualityBadge score={row.match_score} className="shrink-0" />
                  ) : null
                }
                footer={
                  <div className="flex flex-wrap gap-2">
                    <Button type="button" size="sm" onClick={() => navigate(interviewHref)}>
                      <Video className="size-3.5" aria-hidden />
                      Interview now
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        openCanvas({
                          title: `Schedule · ${person.name}`,
                          content: (
                            <ScheduleInterviewCanvas
                              applicationId={row.id}
                              candidateName={person.name}
                              onClose={closeCanvas}
                            />
                          ),
                        })
                      }
                    >
                      <CalendarPlus className="size-3.5" aria-hidden />
                      Schedule
                    </Button>
                    {row.brief_id ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          openCanvas({
                            title: `Kit · ${person.name}`,
                            content: (
                              <div className="h-full overflow-y-auto p-4">
                                <InterviewKitPanel
                                  briefId={row.brief_id as string}
                                  applicationId={row.id}
                                  candidateName={person.name}
                                  interviewId={row.next_interview?.id}
                                />
                              </div>
                            ),
                          })
                        }
                      >
                        <ClipboardList className="size-3.5" aria-hidden />
                        Kit
                      </Button>
                    ) : null}
                    {analysisId ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghostMuted"
                        onClick={() => {
                          navigate(
                            getHrefWithThreadPreservation(
                              `/interviewer/evaluations/${analysisId}`,
                            ),
                            {
                              state: {
                                label: person.name,
                                backPath: '/interviewer/candidates',
                                backLabel: 'Candidates',
                              },
                            },
                          )
                        }}
                      >
                        View analysis
                      </Button>
                    ) : null}
                  </div>
                }
              />
            )
          })}
        </PaneResponsiveCardGrid>
      )}
    </PageContainer>
  )
}
